import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { todayKey } from "../../shared/dates";

// The nudge engine (moved out of App.jsx 30 Oct 2026): every minute while the app is open it picks the most
// important nudge that is due and shows it, at most one every NUDGE_MIN_GAP_MINS, each kind no more often than its
// cooldown. NudgeApp owns the state it reads and saves.

const NUDGE_CATEGORIES = {
  1: "timing", 2: "timing", 3: "timing", 4: "timing", 5: "timing",
  6: "deadlines", 7: "deadlines", 8: "deadlines", 9: "deadlines",
  11: "habits", 12: "habits", 13: "habits",
  14: "top10", 19: "top10", 20: "top10", 21: "top10", 22: "top10", 23: "top10",
  "15a": "today", "15b": "today", "15c": "today",
  16: "mindset", 17: "mindset", 18: "mindset",
  24: "longterm", 25: "longterm", 26: "longterm", 28: "longterm",
  29: "top10",
};

export const NUDGE_CATEGORY_DEFS = [
  { key: "timing",    label: "Timing",       icon: "⏰" },
  { key: "deadlines", label: "Deadlines",    icon: "📅" },
  { key: "top10",     label: "Top 10",       icon: "🏆" },
  { key: "longterm",  label: "Goals",        icon: "🌱" },
  { key: "today",     label: "Today's Plan", icon: "📋" },
  { key: "habits",    label: "Habits",       icon: "🔥" },
  { key: "mindset",   label: "Mindset",      icon: "💡" },
  { key: "chat",      label: "Chat",         icon: "💬" },
];

const NUDGE_COOLDOWNS = {
  1:     12 * 60,      // phase start — once per phase per day
  2:     24 * 60,      // task start — once per task per day
  3:     8 * 60,       // unplanned day — 8 hours
  4:     12 * 60,      // end of phase — once per phase per day
  5:     2 * 60,       // check-in reminder — 2 hours
  6:     24 * 60,      // deadline today — once per day (aggregated)
  7:     24 * 60,      // overdue — once per day (aggregated)
  8:     48 * 60,      // deadline 1-3 days — once per 2 days (aggregated)
  9:     3 * 24 * 60,  // deadline 4-14 days — once per 3 days per task
  11:    8 * 60,       // no activity today — 8 hours
  12:    24 * 60,      // streak at risk — once per day
  13:    5 * 24 * 60,  // extended absence — once per 5 days
  14:    7 * 24 * 60,  // top 10 sparse — once per week
  "15a": 24 * 60,      // low commitment scheduled — once per day per task
  "15b": 24 * 60,      // high commitment scheduled — once per day per task
  "15c": 24 * 60,      // medium commitment scheduled — once per day per task
  16:    8 * 60,       // win statement missing — 8 hours
  17:    12 * 60,      // momentum nudge — 12 hours
  18:    24 * 60,      // dreaded task — once per day per task
  19:    7 * 24 * 60,  // top 10 not set up — once per week
  20:    24 * 60,      // top 10 not scheduled — once per day (aggregated)
  21:    72 * 60,      // top 10 item completed — once per 3 days
  22:    14 * 24 * 60, // top 10 has room — once per 2 weeks
  23:    7 * 24 * 60,  // all top 10 done — once per week
  24:    7 * 24 * 60,  // medium-term check-in — once per week
  25:    14 * 24 * 60, // long-term reminder — once per 2 weeks
  26:    7 * 24 * 60,  // stale medium-term — once per week
  28:    3 * 24 * 60,  // inbox piling up — once per 3 days
  29:    30 * 24 * 60, // top10 milestone — once per 30 days
};

const NUDGE_PRIORITY = [6,7,2,5,8,4,1,12,18,9,3,"15b","15c","15a",16,17,11,20,24,14,19,22,25,26,28,13,21,23,29];
const NUDGE_MIN_GAP_MINS = 30;

// The nudge state after the user dismisses a nudge (Dismiss or Next): it joins the dismissed ones and leaves the card.
const withDismissed = (state, nudgeId) => ({ ...state, userDismissed: [...(state?.userDismissed || []), nudgeId].slice(-200), lastActiveNudge: null });

export function useNudgeEngine({ tasks, todayData, settings, compass, nudgeState, saveNudgeState, saveNudge, uid, onNewNudge, top10Completions }) {
  const nudgeStateRef = useRef(nudgeState);
  const onNewNudgeRef = useRef(onNewNudge);

  useEffect(() => {
    nudgeStateRef.current = nudgeState;
  }, [nudgeState]);

  useEffect(() => {
    onNewNudgeRef.current = onNewNudge;
  }, [onNewNudge]);

  const toMins = (t) => {
    if (!t) return 0;
    const [h, m] = (t || "00:00").split(":").map(Number);
    return h * 60 + (m || 0);
  };

  const parseTimeTo24Mins = (t) => {
    if (!t) return null;
    // HH:MM 24-hour format (from time input)
    const hhmm = t.match(/^(\d{1,2}):(\d{2})$/);
    if (hhmm) return parseInt(hhmm[1]) * 60 + parseInt(hhmm[2]);
    // 12-hour format e.g. "6pm", "5:30pm"
    const m = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
    if (!m) return null;
    let h = parseInt(m[1]), min = parseInt(m[2] || "0");
    const ampm = (m[3] || "").toLowerCase();
    if (ampm === "pm" && h !== 12) h += 12;
    if (ampm === "am" && h === 12) h = 0;
    return h * 60 + min;
  };

  const nowMins = () => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); };
  const nowTs   = () => Date.now();
  const todayStr = () => new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const nowTimeStr = () => new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

  // Picks the nudge due now and shows it, returning true; false when there is none. `now` is Next on the Nudge card:
  // the user asked for the next one, so it doesn't wait out the gap since the last nudge or a snooze. `peek` only says
  // whether there is one, showing and saving nothing: the card shows Next only then.
  const evaluate = useCallback(({ now = false, peek = false } = {}) => {
    if (!uid) return false;

    const canShow = (key, cooldownMins) => {
      const last = nudgeStateRef.current?.lastShown?.[key] || 0;
      return (nowTs() - last) > cooldownMins * 60 * 1000;
    };

    const isDismissed = (key) => (nudgeStateRef.current?.dismissed || []).includes(key);

    // Up to three quoted task names, then how many more: each name is a link, and a long run of them can't be read.
    const namesList = (list) => list.slice(0, 3).map(t => `"${t.text}"`).join(", ") + (list.length > 3 ? `, and ${list.length - 3} more` : "");
    const makeNudge = (type, message, taskId = null) => ({
      id: `nudge_${type}_${taskId || "global"}_${Date.now()}`,
      nudgeType: type,
      nudgeCategory: NUDGE_CATEGORIES[type] || "mindset",
      type: "Nudge",
      message,
      taskId,
      date: todayStr(),
      time: nowTimeStr(),
      ts: nowTs(),
    });

    const catEnabled = (type) => {
      const cat = NUDGE_CATEGORIES[type] || NUDGE_CATEGORIES[String(type)];
      if (!cat) return true;
      return settings?.[`nudge_${cat}`] !== false;
    };

    const pushIfEnabled = (type, priorityIdx, nudge, key) => {
      if (!catEnabled(type)) return;
      candidates.push({ priority: priorityIdx, nudge, key });
    };
    const nm = nowMins();
    const morningStart    = toMins(settings?.dayStart       || "06:00");
    const afternoonStart  = toMins(settings?.afternoonStart || "12:00");
    const eveningStart    = toMins(settings?.eveningStart   || "17:00");
    const dayEnd          = toMins(settings?.dayEnd         || "23:00");

    const currentPhase = nm >= eveningStart ? "evening" : nm >= afternoonStart ? "afternoon" : nm >= morningStart ? "morning" : null;
    const todayTasks  = (tasks || []).filter(t => t.todayPhase && !t.done);
    const doneTasks   = (tasks || []).filter(t => t.todayPhase && t.done);
    const top10Tasks  = (tasks || [])
      .filter(t => t.bubbleSortOrder != null && !t.done)
      .sort((a, b) => a.bubbleSortOrder - b.bubbleSortOrder)
      .slice(0, 10);
    const allTasks    = tasks || [];

    const candidates = [];

    // ── Type 1: Phase start reminder ─────────────────────────────────
    if (currentPhase) {
      const key = `1_${currentPhase}_${new Date().toDateString()}`;
      const phaseTasks = todayTasks.filter(t => t.todayPhase === currentPhase);
      const phaseStart = currentPhase === "morning" ? morningStart : currentPhase === "afternoon" ? afternoonStart : eveningStart;
      if (Math.abs(nm - phaseStart) <= 30 && phaseTasks.length > 0 && canShow(key, NUDGE_COOLDOWNS[1]) && !isDismissed(key)) {
        const first = phaseTasks.sort((a, b) => (parseTimeTo24Mins(a.todayTime) || 999) - (parseTimeTo24Mins(b.todayTime) || 999))[0];
        pushIfEnabled(1, NUDGE_PRIORITY.indexOf(1), makeNudge(1, `${currentPhase.charAt(0).toUpperCase() + currentPhase.slice(1)} is starting. You have ${phaseTasks.length} task${phaseTasks.length > 1 ? "s" : ""} planned. First up: "${first.text}".`, first.id), key);
      }
    }

    // ── Type 2: Task start reminder ──────────────────────────────────
    todayTasks.forEach(t => {
      if (!t.todayTime) return;
      const taskMins = parseTimeTo24Mins(t.todayTime);
      if (taskMins === null) return;
      const key = `2_${t.id}_${new Date().toDateString()}`;
      if (nm >= taskMins && nm <= taskMins + 45 && canShow(key, NUDGE_COOLDOWNS[2]) && !isDismissed(key)) {
        pushIfEnabled(2, NUDGE_PRIORITY.indexOf(2), makeNudge(2, `You've got "${t.text}" scheduled for ${t.todayTime}. Have you started it yet?`, t.id), key);
      }
    });

    // ── Type 3: Unplanned day ────────────────────────────────────────
    if (nm >= morningStart + 60 && nm < afternoonStart && !todayData?.planned) {
      const key = `3_${new Date().toDateString()}`;
      if (canShow(key, NUDGE_COOLDOWNS[3]) && !isDismissed(key)) {
        pushIfEnabled(3, NUDGE_PRIORITY.indexOf(3), makeNudge(3, "You haven't planned your day yet. What are you working on today?"), key);
      }
    }

    // ── Type 4: End of phase wrap-up ─────────────────────────────────
    if (currentPhase) {
      const phaseEnd = currentPhase === "morning" ? afternoonStart : currentPhase === "afternoon" ? eveningStart : dayEnd;
      const phaseUndone = todayTasks.filter(t => t.todayPhase === currentPhase);
      const key = `4_${currentPhase}_${new Date().toDateString()}`;
      if (Math.abs(nm - phaseEnd) <= 20 && phaseUndone.length > 0 && canShow(key, NUDGE_COOLDOWNS[4]) && !isDismissed(key)) {
        pushIfEnabled(4, NUDGE_PRIORITY.indexOf(4), makeNudge(4, `${currentPhase.charAt(0).toUpperCase() + currentPhase.slice(1)} is wrapping up. ${phaseUndone.length} task${phaseUndone.length > 1 ? "s aren't" : " isn't"} checked off yet.`), key);
      }
    }

    // ── Type 5: Check-in reminder ────────────────────────────────────
    if (todayData?.checkIn?.time) {
      const ciMins = parseTimeTo24Mins(todayData.checkIn.time);
      const key = `5_${todayData.checkIn.time}_${new Date().toDateString()}`;
      if (ciMins !== null && nm > ciMins + 5 && nm < ciMins + 60 && canShow(key, NUDGE_COOLDOWNS[5]) && !isDismissed(key)) {
        const ctx = todayData.checkIn.context ? ` — ${todayData.checkIn.context}` : "";
        pushIfEnabled(5, NUDGE_PRIORITY.indexOf(5), makeNudge(5, `You set a check-in for ${todayData.checkIn.time}${ctx}. How did it go?`), key);
      }
    }

    // ── Types 6-9: Deadline nudges (aggregated) ──────────────────────
    const today = new Date(); today.setHours(0,0,0,0);
    const deadlineTasks = allTasks.filter(t => t.deadline && !t.done).map(t => {
      const dl = new Date(t.deadline + "T00:00:00"); dl.setHours(0,0,0,0);
      return { ...t, daysLeft: Math.round((dl - today) / (1000 * 60 * 60 * 24)) };
    });

    // Type 7: Overdue (aggregated)
    {
      const overdue = deadlineTasks.filter(t => t.daysLeft < 0);
      const key = `7_${new Date().toDateString()}`;
      if (overdue.length > 0 && canShow(key, NUDGE_COOLDOWNS[7]) && !isDismissed(key)) {
        const msg = overdue.length === 1
          ? `"${overdue[0].text}" was due ${Math.abs(overdue[0].daysLeft)} day${Math.abs(overdue[0].daysLeft) > 1 ? "s" : ""} ago and isn't done yet.`
          : `You have ${overdue.length} overdue tasks: ${namesList(overdue)}.`;
        pushIfEnabled(7, NUDGE_PRIORITY.indexOf(7), makeNudge(7, msg, overdue.length === 1 ? overdue[0].id : null), key);
      }
    }

    // Type 6: Due today (aggregated)
    {
      const dueToday = deadlineTasks.filter(t => t.daysLeft === 0);
      const key = `6_${new Date().toDateString()}`;
      if (dueToday.length > 0 && canShow(key, NUDGE_COOLDOWNS[6]) && !isDismissed(key)) {
        const msg = dueToday.length === 1
          ? `"${dueToday[0].text}" is due today and isn't on your plan.`
          : `${dueToday.length} tasks are due today: ${namesList(dueToday)}.`;
        pushIfEnabled(6, NUDGE_PRIORITY.indexOf(6), makeNudge(6, msg, dueToday.length === 1 ? dueToday[0].id : null), key);
      }
    }

    // Type 8: Due in 1-3 days (aggregated)
    {
      const soon = deadlineTasks.filter(t => t.daysLeft > 0 && t.daysLeft <= 3);
      const key = `8_${new Date().toDateString()}`;
      if (soon.length > 0 && canShow(key, NUDGE_COOLDOWNS[8]) && !isDismissed(key)) {
        const msg = soon.length === 1
          ? `"${soon[0].text}" is due in ${soon[0].daysLeft} day${soon[0].daysLeft > 1 ? "s" : ""}. Make sure it's on your plan.`
          : `${soon.length} tasks are due in the next 3 days: ${namesList(soon)}.`;
        pushIfEnabled(8, NUDGE_PRIORITY.indexOf(8), makeNudge(8, msg, soon.length === 1 ? soon[0].id : null), key);
      }
    }

    // Type 9: Due in 4-14 days (per task)
    deadlineTasks.filter(t => t.daysLeft > 3 && t.daysLeft <= 14).forEach(t => {
      const key = `9_${t.id}`;
      if (canShow(key, NUDGE_COOLDOWNS[9]) && !isDismissed(key))
        pushIfEnabled(9, NUDGE_PRIORITY.indexOf(9), makeNudge(9, `"${t.text}" is due in ${t.daysLeft} days. No pressure yet — but worth blocking some time soon.`, t.id), key);
    });

    // ── Type 11: No activity today ───────────────────────────────────
    if (nm >= morningStart + 120) {
      const key = `11_${new Date().toDateString()}`;
      const hasActivity = todayTasks.length > 0 || doneTasks.length > 0;
      if (!hasActivity && canShow(key, NUDGE_COOLDOWNS[11]) && !isDismissed(key))
        pushIfEnabled(11, NUDGE_PRIORITY.indexOf(11), makeNudge(11, "Haven't seen you today — what's the plan?"), key);
    }

    // ── Type 12: Streak at risk ──────────────────────────────────────
    if (nm >= eveningStart) {
      const key = `12_${new Date().toDateString()}`;
      if (doneTasks.length === 0 && canShow(key, NUDGE_COOLDOWNS[12]) && !isDismissed(key))
        pushIfEnabled(12, NUDGE_PRIORITY.indexOf(12), makeNudge(12, "No tasks completed yet today. Finish one thing before the day ends to keep your momentum going."), key);
    }

    // ── Type 13: Extended absence ────────────────────────────────────
    // Requires lastSessionDate from stats — skip if not available
    // (will be wired in when stats are passed to engine)

    // ── Type 14: Bubble Up sparse — prompt to rate tasks ─────────────
    {
      const key = `14_${new Date().toDateString()}`;
      const unrated = allTasks.filter(t => !t.done && t.bubbleSortOrder == null).length;
      if (unrated >= 3 && canShow(key, NUDGE_COOLDOWNS[14]) && !isDismissed(key))
        pushIfEnabled(14, NUDGE_PRIORITY.indexOf(14), makeNudge(14, `You have ${unrated} tasks without a priority ranking. Prioritize to sort what matters most.`), key);
    }

    // ── Types 15a/15b/15c: Today's Plan — commitment-aware ───────────
    todayTasks.forEach(t => {
      if (t.commitment == null) return;
      if (t.commitment <= 6) {
        const key = `15a_${t.id}_${new Date().toDateString()}`;
        if (canShow(key, NUDGE_COOLDOWNS["15a"]) && !isDismissed(key))
          pushIfEnabled("15a", NUDGE_PRIORITY.indexOf("15a"), makeNudge("15a", `"${t.text}" is on your plan today but your commitment is only ${t.commitment}/10. Is it really happening today? If not, be honest and remove it.`, t.id), key);
      } else if (t.commitment >= 10) {
        const key = `15b_${t.id}_${new Date().toDateString()}`;
        if (canShow(key, NUDGE_COOLDOWNS["15b"]) && !isDismissed(key))
          pushIfEnabled("15b", NUDGE_PRIORITY.indexOf("15b"), makeNudge("15b", `You rated "${t.text}" 10/10. You said this one matters — time to make it happen.`, t.id), key);
      } else {
        const key = `15c_${t.id}_${new Date().toDateString()}`;
        if (canShow(key, NUDGE_COOLDOWNS["15c"]) && !isDismissed(key))
          pushIfEnabled("15c", NUDGE_PRIORITY.indexOf("15c"), makeNudge("15c", `Your commitment on "${t.text}" is ${t.commitment}/10. How do we get it to a 10 and take action — or should it come off your plan for today?`, t.id), key);
      }
    });

    // ── Type 16: Win statement missing ───────────────────────────────
    if (todayData?.planned && !todayData?.win && nm >= morningStart) {
      const key = `16_${new Date().toDateString()}`;
      if (canShow(key, NUDGE_COOLDOWNS[16]) && !isDismissed(key))
        pushIfEnabled(16, NUDGE_PRIORITY.indexOf(16), makeNudge(16, "What would make today a win? Think about that and set your intention on the Today page."), key);
    }

    // ── Type 17: Momentum nudge ──────────────────────────────────────
    if (currentPhase) {
      const phaseDone   = doneTasks.filter(t => t.todayPhase === currentPhase);
      const phaseUndone = todayTasks.filter(t => t.todayPhase === currentPhase);
      const key = `17_${currentPhase}_${new Date().toDateString()}`;
      if (phaseDone.length > 0 && phaseUndone.length === 0 && canShow(key, NUDGE_COOLDOWNS[17]) && !isDismissed(key)) {
        const nextPhase = currentPhase === "morning" ? "afternoon" : currentPhase === "afternoon" ? "evening" : null;
        if (nextPhase) pushIfEnabled(17, NUDGE_PRIORITY.indexOf(17), makeNudge(17, `You cleared your ${currentPhase}. Strong start — what's the priority for this ${nextPhase}?`), key);
      }
    }

    // ── Type 18: Dreaded task scheduled ─────────────────────────────
    todayTasks.filter(t => t.emotion === "dreading").forEach(t => {
      const key = `18_${t.id}_${new Date().toDateString()}`;
      if (canShow(key, NUDGE_COOLDOWNS[18]) && !isDismissed(key))
        pushIfEnabled(18, NUDGE_PRIORITY.indexOf(18), makeNudge(18, `You've got "${t.text}" on the plan today — the one you're dreading. Start with just 10 minutes.`, t.id), key);
    });

    // ── Type 19: No Bubble Up ratings yet ────────────────────────────
    {
      const key = `19_${new Date().toDateString()}`;
      if (top10Tasks.length === 0 && allTasks.filter(t => !t.done).length >= 3 && canShow(key, NUDGE_COOLDOWNS[19]) && !isDismissed(key))
        pushIfEnabled(19, NUDGE_PRIORITY.indexOf(19), makeNudge(19, "You haven't ranked your tasks yet. Prioritize and rate what matters most — it takes less than a minute."), key);
    }

    // ── Type 20: Top 10 not scheduled (aggregated) ───────────────────
    {
      const unscheduled = top10Tasks.filter(t => !t.todayPhase);
      const key = `20_${new Date().toDateString()}`;
      if (unscheduled.length > 0 && canShow(key, NUDGE_COOLDOWNS[20]) && !isDismissed(key)) {
        const msg = unscheduled.length === 1
          ? `"${unscheduled[0].text}" is in your Top 10 but hasn't been on your plan lately. Is it time to schedule it?`
          : `${unscheduled.length} of your Top 10 haven't been on your plan lately: ${unscheduled.slice(0, 3).map(t => `"${t.text}"`).join(", ")}${unscheduled.length > 3 ? " and more" : ""}.`;
        pushIfEnabled(20, NUDGE_PRIORITY.indexOf(20), makeNudge(20, msg, unscheduled.length === 1 ? unscheduled[0].id : null), key);
      }
    }

    // ── Type 21: Retired — slot-based Top 10 replaced by Bubble Up ───
    // (previously fired when completed Top 10 items left open slots)

    // ── Type 22: Retired — slot-based Top 10 replaced by Bubble Up ───

    // ── Type 23: All priority tasks done ─────────────────────────────
    {
      const key = `23_${new Date().toDateString()}`;
      if (top10Tasks.length >= 5 && top10Tasks.every(t => t.done) && canShow(key, NUDGE_COOLDOWNS[23]) && !isDismissed(key))
        pushIfEnabled(23, NUDGE_PRIORITY.indexOf(23), makeNudge(23, "You've completed everything in your priority list. Time to Prioritize and decide what's next."), key);
    }

    // ── Type 24: Medium-term check-in ────────────────────────────────
    {
      const key = `24_weekly`;
      const medTasks = allTasks.filter(t => t.tier === "medterm" && !t.done && !(t.bubbleSortOrder != null && t.bubbleSortOrder <= 10) && !t.todayPhase);
      if (medTasks.length > 0 && canShow(key, NUDGE_COOLDOWNS[24]) && !isDismissed(key)) {
        const pick = medTasks[Math.floor(Math.random() * medTasks.length)];
        pushIfEnabled(24, NUDGE_PRIORITY.indexOf(24), makeNudge(24, `You've got "${pick.text}" sitting in your medium-term list. Is it time to move it forward?`, pick.id), key);
      }
    }

    // ── Type 25: Long-term goal reminder ─────────────────────────────
    {
      const key = `25_biweekly`;
      const longTasks = allTasks.filter(t => t.tier === "longterm" && !t.done && !t.inTop10);
      if (longTasks.length > 0 && canShow(key, NUDGE_COOLDOWNS[25]) && !isDismissed(key)) {
        const pick = longTasks[Math.floor(Math.random() * longTasks.length)];
        pushIfEnabled(25, NUDGE_PRIORITY.indexOf(25), makeNudge(25, `A reminder of something you said matters in the long run: "${pick.text}". Is it getting the attention it deserves?`, pick.id), key);
      }
    }

    // ── Type 26: Stale medium-term item ──────────────────────────────
    // (Requires createdAt timestamp — skipping until task creation stamps are added)

    // ── Type 27: Removed (replaced by 15a/15b/15c) ───────────────────

    // ── Type 28: Inbox items piling up ───────────────────────────────
    {
      const key = `28_${new Date().toDateString()}`;
      const inboxTasks = allTasks.filter(t => t.tier === "inbox" && !t.done);
      if (inboxTasks.length >= 3 && canShow(key, NUDGE_COOLDOWNS[28]) && !isDismissed(key))
        pushIfEnabled(28, NUDGE_PRIORITY.indexOf(28), makeNudge(28, `You've got ${inboxTasks.length} tasks in your Someday list without a timeframe. Take 5 minutes to sort them.`), key);
    }

    // ── Type 29: Top 10 completions milestone ────────────────────────
    {
      const MILESTONES = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100];
      const reached = MILESTONES.filter(m => (top10Completions || 0) >= m);
      if (reached.length > 0) {
        const key = `29_milestone_${reached[reached.length - 1]}`;
        if (canShow(key, NUDGE_COOLDOWNS[29]) && !isDismissed(key)) {
          const total = top10Completions;
          const nextMilestone = MILESTONES.find(m => m > total);
          const msg = nextMilestone
            ? `You've completed ${total} Top 10 goals since you started. ${nextMilestone - total} away from ${nextMilestone} — keep building.`
            : `You've completed ${total} Top 10 goals. That's a serious track record.`;
          pushIfEnabled(29, NUDGE_PRIORITY.indexOf(29), makeNudge(29, msg), key);
        }
      }
    }

    const snoozeUntil = nudgeStateRef.current?.snoozeUntil || 0;
    const lastNudgeTs = nudgeStateRef.current?.lastNudgeTs || 0;
    const snoozed = Date.now() < snoozeUntil;
    const tooSoon = (Date.now() - lastNudgeTs) < NUDGE_MIN_GAP_MINS * 60 * 1000;

    if (candidates.length === 0 || (!now && (snoozed || tooSoon))) return false;
    if (peek) return true;

    // Pick highest priority candidate
    candidates.sort((a, b) => a.priority - b.priority);
    const winner = candidates[0];

    // Record lastShown, lastNudgeTs, lastActiveNudge and save to history
    const newLastShown = { ...(nudgeStateRef.current?.lastShown || {}), [winner.key]: nowTs() };
    // Into the ref at once as well, so a second Next before the app redraws doesn't pick the same nudge again.
    const shownState = { ...nudgeStateRef.current, lastShown: newLastShown, lastNudgeTs: nowTs(), lastActiveNudge: winner.nudge };
    nudgeStateRef.current = shownState;
    saveNudgeState(shownState);
    saveNudge(winner.nudge);
    onNewNudgeRef.current?.(winner.nudge);
    return true;

  }, [tasks, todayData, settings, uid, saveNudgeState, saveNudge, top10Completions]);

  const evaluateRef = useRef(evaluate);
  useEffect(() => { evaluateRef.current = evaluate; }, [evaluate]);

  // Run every 60 seconds. Initial display is handled by the app root on Firestore load. The minute also refreshes
  // hasNext, since a kind's wait can run out with nothing else changing.
  const [minute, setMinute] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => { evaluateRef.current(); setMinute(m => m + 1); }, 60 * 1000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const snooze = useCallback((minutes) => {
    const snoozeUntil = Date.now() + minutes * 60 * 1000;
    saveNudgeState({ ...nudgeStateRef.current, snoozeUntil, lastActiveNudge: null });
  }, [saveNudgeState]);

  const gotIt = useCallback((nudgeId) => saveNudgeState(withDismissed(nudgeStateRef.current, nudgeId)), [saveNudgeState]);
  // Next (Craig, 30 Oct 2026: "one for 'Dismiss' (which doesn't surface the next nudge) and one for 'Next' (which
  // dismisses the current nudge and presents the next one)"). The dismissal goes into the ref first, so the next
  // nudge's save keeps it. Returns whether there was a next one.
  const next = useCallback((nudgeId) => {
    const state = withDismissed(nudgeStateRef.current, nudgeId);
    nudgeStateRef.current = state;
    if (evaluateRef.current({ now: true })) return true;
    saveNudgeState(state);
    return false;
  }, [saveNudgeState]);

  const pinNudge = useCallback((nudge) => {
    saveNudgeState({ ...nudgeStateRef.current, pinnedNudge: nudge, pinnedDate: todayKey() });
  }, [saveNudgeState]);

  const unpinNudge = useCallback(() => {
    saveNudgeState({ ...nudgeStateRef.current, pinnedNudge: null, pinnedDate: null });
  }, [saveNudgeState]);

  // Whether Next has a nudge to show (Craig, 30 Oct 2026: "Yes", to showing Next only when one is due): worked out again
  // when the tasks, the day, the settings or the nudge state change, and each minute.
  const hasNext = useMemo(() => evaluate({ now: true, peek: true }), [evaluate, nudgeState, minute]); // eslint-disable-line react-hooks/exhaustive-deps

  return { snooze, gotIt, next, hasNext, pinNudge, unpinNudge };
}
